/**
 * NotificationBell - Bell icon with dropdown for user notifications
 * Shows unread count badge and organized notifications by category
 */
import { useState, useEffect, useRef } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Bell,
  Check,
  CheckCheck,
  X,
  Settings,
  Sparkles,
  Calendar,
  Users,
  AlertCircle,
  Loader2
} from "lucide-react";

// Icon map for notification types
const NOTIFICATION_ICONS = {
  default_added: Sparkles,
  default_published: Sparkles,
  event_update: Calendar,
  rsvp_update: Users,
  platform_update: Settings,
  personal: AlertCircle
};

// Color map for notification sources
const SOURCE_COLORS = {
  platform: "bg-violet-100 text-violet-700",
  event: "bg-blue-100 text-blue-700",
  personal: "bg-amber-100 text-amber-700"
};

export default function NotificationBell({ className = "" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState(false);
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  // Fetch notifications
  const fetchNotifications = async () => {
    const token = localStorage.getItem("session_token");
    if (!token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/notifications?limit=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    }
    setLoading(false);
  };
  
  // Fetch on mount and periodically
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Every minute
    return () => clearInterval(interval);
  }, []);
  
  // Mark single notification as read
  const markAsRead = async (notificationId) => {
    const token = localStorage.getItem("session_token");
    if (!token) return;
    
    try {
      await fetch(`${API}/notifications/${notificationId}/read`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };
  
  // Mark all as read
  const markAllAsRead = async () => {
    const token = localStorage.getItem("session_token");
    if (!token) return;
    
    setMarking(true);
    try {
      await fetch(`${API}/notifications/read-all`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
    setMarking(false);
  };
  
  // Group notifications by source
  const groupedNotifications = {
    platform: notifications.filter(n => n.source === "platform"),
    event: notifications.filter(n => n.source === "event"),
    personal: notifications.filter(n => n.source === "personal")
  };
  
  // Format relative time
  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };
  
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="sm"
        className="relative p-2"
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) fetchNotifications();
        }}
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>
      
      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
            <h3 className="font-semibold text-sm">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-violet-600"
                  onClick={markAllAsRead}
                  disabled={marking}
                >
                  {marking ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <CheckCheck className="w-3 h-3 mr-1" />
                  )}
                  Mark all read
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => setIsOpen(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Content */}
          <ScrollArea className="max-h-[400px]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y">
                {/* Platform Updates */}
                {groupedNotifications.platform.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-violet-50 text-[10px] font-semibold text-violet-700 uppercase tracking-wider">
                      Platform Updates
                    </div>
                    {groupedNotifications.platform.map((notif) => (
                      <NotificationItem
                        key={notif.notification_id}
                        notification={notif}
                        onMarkRead={markAsRead}
                        formatTime={formatTime}
                      />
                    ))}
                  </div>
                )}
                
                {/* Event Updates */}
                {groupedNotifications.event.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-blue-50 text-[10px] font-semibold text-blue-700 uppercase tracking-wider">
                      Event Updates
                    </div>
                    {groupedNotifications.event.map((notif) => (
                      <NotificationItem
                        key={notif.notification_id}
                        notification={notif}
                        onMarkRead={markAsRead}
                        formatTime={formatTime}
                      />
                    ))}
                  </div>
                )}
                
                {/* Personal */}
                {groupedNotifications.personal.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-amber-50 text-[10px] font-semibold text-amber-700 uppercase tracking-wider">
                      Personal
                    </div>
                    {groupedNotifications.personal.map((notif) => (
                      <NotificationItem
                        key={notif.notification_id}
                        notification={notif}
                        onMarkRead={markAsRead}
                        formatTime={formatTime}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}

// Individual notification item
function NotificationItem({ notification, onMarkRead, formatTime }) {
  const Icon = NOTIFICATION_ICONS[notification.type] || Bell;
  const sourceColor = SOURCE_COLORS[notification.source] || "bg-gray-100 text-gray-700";
  
  return (
    <div
      className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${
        !notification.read ? "bg-violet-50/30" : ""
      }`}
      onClick={() => !notification.read && onMarkRead(notification.notification_id)}
      data-testid={`notification-${notification.notification_id}`}
    >
      <div className="flex gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${sourceColor}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${!notification.read ? "font-medium" : "text-gray-700"}`}>
            {notification.message}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-gray-500">
              {formatTime(notification.created_at)}
            </span>
            {notification.category && (
              <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">
                {notification.category}
              </Badge>
            )}
          </div>
        </div>
        {!notification.read && (
          <div className="w-2 h-2 rounded-full bg-violet-500 flex-shrink-0 mt-2" />
        )}
      </div>
    </div>
  );
}
